const DEV_TELEGRAM_ID = import.meta.env.VITE_DEV_TELEGRAM_ID;

function getWebApp() {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp || null;
}

function callWebApp(method, ...args) {
  const fn = getWebApp()?.[method];
  if (typeof fn !== 'function') return undefined;
  return fn(...args);
}

function createBrowserStorage(storageName) {
  return {
    get(key) {
      if (typeof window === 'undefined') return null;
      try {
        return window[storageName]?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key, value) {
      if (typeof window === 'undefined') return;
      try {
        window[storageName]?.setItem(key, String(value));
      } catch {
        /* storage failures are non-fatal */
      }
    },
    remove(key) {
      if (typeof window === 'undefined') return;
      try {
        window[storageName]?.removeItem(key);
      } catch {
        /* storage failures are non-fatal */
      }
    },
    getItem(key) {
      return this.get(key);
    },
    setItem(key, value) {
      this.set(key, value);
    },
    removeItem(key) {
      this.remove(key);
    },
  };
}

export function isHostAvailable() {
  return Boolean(getWebApp());
}

export function getInitData() {
  return getWebApp()?.initData || '';
}

export function getUser() {
  const user = getWebApp()?.initDataUnsafe?.user;
  if (!user) return null;
  return {
    id: user.id,
    username: user.username,
    firstName: user.first_name,
    lastName: user.last_name,
    languageCode: user.language_code,
    isPremium: user.is_premium,
  };
}

export function getLanguageCode() {
  return getUser()?.languageCode || '';
}

export function getPlatform() {
  return getWebApp()?.platform || '';
}

export function isIOSPlatform(
  telegramPlatform = getPlatform(),
  userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '',
) {
  const p = telegramPlatform || '';
  const ua = userAgent || '';
  return /^(ios)$/i.test(p) || /iphone|ipod|ipad/i.test(p) || /iPhone|iPod|iPad/i.test(ua);
}

export function getLaunchParam() {
  return getWebApp()?.initDataUnsafe?.start_param || '';
}

export function getAuthHeaders() {
  const WebApp = getWebApp();
  const headers = {};
  const telegramId = WebApp?.initDataUnsafe?.user?.id;
  const startParam = WebApp?.initDataUnsafe?.start_param;
  const initData = WebApp?.initData;

  if (telegramId) {
    headers['X-Telegram-Id'] = telegramId.toString();
  } else if (DEV_TELEGRAM_ID) {
    headers['X-Telegram-Id'] = String(DEV_TELEGRAM_ID);
  }

  if (startParam) headers['X-Telegram-Start-Param'] = startParam;
  if (initData) headers['x-telegram-init-data'] = initData;

  return headers;
}

export const storage = createBrowserStorage('localStorage');
export const session = createBrowserStorage('sessionStorage');

export const haptics = {
  selection() {
    const fn = getWebApp()?.HapticFeedback?.selectionChanged;
    if (typeof fn !== 'function') return false;
    fn();
    return true;
  },
  impact(style = 'light') {
    const fn = getWebApp()?.HapticFeedback?.impactOccurred;
    if (typeof fn !== 'function') return false;
    fn(style);
    return true;
  },
  notification(type = 'success') {
    const fn = getWebApp()?.HapticFeedback?.notificationOccurred;
    if (typeof fn !== 'function') return false;
    fn(type);
    return true;
  },
};

export function haptic(type = 'medium') {
  if (type === 'success' || type === 'error' || type === 'warning') {
    return haptics.notification(type);
  } else {
    return haptics.impact(type);
  }
}

function openInvoice(url, callback) {
  const fn = getWebApp()?.openInvoice;
  if (typeof fn !== 'function') return false;
  fn(url, callback);
  return true;
}

export function purchase(product, callbacks = {}) {
  const normalized =
    typeof product === 'string' ? { productId: product } : product || {};
  const checkoutUrl = normalized.checkoutUrl || normalized.url;
  const onStatus =
    callbacks.onStatus ||
    normalized.onStatus ||
    normalized.onResult ||
    normalized.callback;

  if (!checkoutUrl) return false;
  return openInvoice(checkoutUrl, (status) => {
    onStatus?.(status);
  });
}

export function openExternalUrl(url) {
  if (!url) return false;
  const fn = getWebApp()?.openLink;
  if (typeof fn !== 'function') return false;
  fn(url);
  return true;
}

function openTelegramShareUrl(url, text = '') {
  if (!url) return false;
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}${
    text ? `&text=${encodeURIComponent(text)}` : ''
  }`;
  const fn = getWebApp()?.openTelegramLink;
  if (typeof fn !== 'function') return false;
  fn(shareUrl);
  return true;
}

export function shareUrl(url, options = {}) {
  if (!url) return false;
  const { text = '' } = options || {};
  if (openTelegramShareUrl(url, text)) return true;

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    navigator.share({ url, text }).catch(() => {});
    return true;
  }

  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(url, '_blank');
    return true;
  }

  return false;
}

export function sharePreparedMessage(preparedMessageId) {
  if (!preparedMessageId) return false;
  const fn = getWebApp()?.shareMessage;
  if (typeof fn !== 'function') return false;
  fn(preparedMessageId);
  return true;
}

export function downloadAsset(asset) {
  const { url, filename } =
    typeof asset === 'string' ? { url: asset, filename: undefined } : asset || {};
  if (!url) return false;
  const fn = getWebApp()?.downloadFile;
  if (typeof fn !== 'function') return false;
  fn({
    url,
    file_name: filename,
  });
  return true;
}

export function showAlert(message) {
  const fn = getWebApp()?.showAlert;
  if (typeof fn === 'function') {
    fn(message);
    return;
  }
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(message);
  }
}

export function showConfirm(message) {
  return new Promise((resolve) => {
    const fn = getWebApp()?.showConfirm;
    if (typeof fn === 'function') {
      fn(message, resolve);
      return;
    }
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      resolve(window.confirm(message));
      return;
    }
    resolve(false);
  });
}

function normalizePopupAction(action, index) {
  if (typeof action === 'string') {
    return { id: action || `action-${index}`, type: 'default', text: action };
  }
  if (!action) return { id: `action-${index}`, type: 'default', text: '' };
  return {
    id: action.id || action.value || `action-${index}`,
    type: action.role === 'destructive' ? 'destructive' : action.role || action.type || 'default',
    text: action.label || action.text || action.id || `Action ${index + 1}`,
  };
}

export function showPopup(params) {
  return new Promise((resolve) => {
    const fn = getWebApp()?.showPopup;
    if (typeof fn !== 'function') {
      resolve(null);
      return;
    }
    const actions = params?.actions || params?.buttons || [];
    fn(
      {
        title: params?.title || '',
        message: params?.message || '',
        buttons: actions.map(normalizePopupAction),
      },
      resolve,
    );
  });
}

export function close() {
  callWebApp('close');
}

export function setBackButton({ visible, onClick } = {}) {
  const WebApp = getWebApp();
  const BackButton = WebApp?.BackButton;
  if (!BackButton) return;

  if (onClick) {
    BackButton.offClick?.(onClick);
    WebApp.offEvent?.('backButtonClicked', onClick);
  }

  if (visible) {
    BackButton.show?.();
    if (onClick) {
      BackButton.onClick?.(onClick);
      WebApp.onEvent?.('backButtonClicked', onClick);
    }
  } else {
    BackButton.hide?.();
  }
}

export function hideBackButton(onClick) {
  setBackButton({ visible: false, onClick });
}

export function setHeaderColor(color) {
  getWebApp()?.setHeaderColor?.(color);
}

export function setBackgroundColor(color) {
  getWebApp()?.setBackgroundColor?.(color);
}

export function setBottomBarColor(color) {
  getWebApp()?.setBottomBarColor?.(color);
}

export function getThemeParams() {
  return getWebApp()?.themeParams || {};
}

export function ready() {
  callWebApp('ready');
}

export function expand() {
  callWebApp('expand');
}

export function enableClosingConfirmation() {
  callWebApp('enableClosingConfirmation');
}

export function disableVerticalSwipes() {
  callWebApp('disableVerticalSwipes');
}

export function requestFullscreen() {
  callWebApp('requestFullscreen');
}

export function exitFullscreen() {
  callWebApp('exitFullscreen');
}

export function initializeShell({ fullscreen = false } = {}) {
  const available = isHostAvailable();
  if (!available) return false;

  ready();
  expand();
  enableClosingConfirmation();
  disableVerticalSwipes();

  if (fullscreen) {
    try {
      requestFullscreen();
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Fullscreen not supported:', error);
    }
  }

  return true;
}

export function dispatchAppEvent(name, detail) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* non-browser / SSR */
  }
}
